import * as React from 'react';
import styled, { css } from 'styled-components';

import { BodyScrollLocker, Overlay } from '../../components';
import { themeGet, is } from '../../theme/utils';

interface OffCanvasContextValue {
  isActive: boolean;
  toggle: (status?: boolean) => void;
}

export const OffCanvasContext = React.createContext({} as OffCanvasContextValue);

interface OffCanvasProviderProps {}

export const OffCanvasProvider: React.FC<OffCanvasProviderProps> = ({ children }) => {
  const [isActive, setIsActive] = React.useState(false);

  const toggle = React.useCallback((status?: boolean) => {
    setIsActive((isActive) => status || !isActive);
  }, []);

  const value = React.useMemo(
    () => ({
      isActive,
      toggle,
    }),
    [isActive, toggle],
  );

  return <OffCanvasContext.Provider value={value}>{children}</OffCanvasContext.Provider>;
};

type Position = 'left' | 'right';

interface OffCanvasContainerProps {
  bodyLockOnActive?: boolean;
  position?: Position;
}

const fromLeft = css<{ isActive: boolean }>`
  transform: translateX(-100%);
  ${is('isActive')`
      transform: translateX(0);
  `}
`;

const fromRight = css<{ isActive: boolean }>`
  transform: translateX(100%);
  right: 0;
  ${is('isActive')`
      transform: translateX(0);
  `}
`;

const OffCanvasInnerWrapper = styled.div<{ isActive: boolean; position?: Position }>`
  top: 0;
  bottom: 0;
  position: fixed;
  height: 100vh;
  z-index: ${themeGet.zIndex('OVERLAY', 5)};
  background: ${themeGet.colors('white')};
  transition: all 0.5s;
  -webkit-overflow-scrolling: touch;
  ${({ position = 'left' }) => (position === 'left' ? fromLeft : fromRight)}
`;

export const OffCanvasWrapper: React.FC<OffCanvasContainerProps> = ({
  children,
  position = 'left',
  bodyLockOnActive = true,
}) => {
  const { isActive, toggle } = React.useContext(OffCanvasContext);
  return (
    <>
      <BodyScrollLocker lock={isActive && bodyLockOnActive}></BodyScrollLocker>
      <OffCanvasInnerWrapper position={position} isActive={isActive}>
        {children}
      </OffCanvasInnerWrapper>
      <Overlay onClick={() => toggle(false)} show={isActive}></Overlay>
    </>
  );
};
